export interface SaveOptions {
  serialize?: boolean;
  onError?: (error: Error) => void;
}

export interface RetrieveOptions<T> {
  serialized?: boolean;
  checkDataType?: (data: unknown) => data is T;
  onError?: (error: Error) => void;
  reviver?: (this: unknown, key: string, value: unknown) => unknown;
}

export interface RemoveOptions {
  onError?: (error: Error) => void;
}

export const saveInStorage = <T>(
  key: string,
  data: T,
  storage: 'local' | 'session',
  options: SaveOptions = {},
): void => {
  const { serialize = true, onError } = options;

  try {
    (storage === 'local' ? window.localStorage : window.sessionStorage).setItem(
      key,
      serialize ? JSON.stringify(data) : (data as string),
    );
  } catch (error) {
    onError?.(error as Error);
  }
  return;
};

export const retrieveFromStorage = <T>(
  key: string,
  storage: 'local' | 'session',
  options: RetrieveOptions<T> = {},
): T | undefined => {
  const {
    serialized = true,
    onError,
    checkDataType = () => true,
    reviver,
  } = options;

  try {
    const data = (
      storage === 'local' ? window.localStorage : window.sessionStorage
    ).getItem(key);

    if (!data) {
      return;
    }

    const processedData: unknown = serialized
      ? JSON.parse(data, reviver)
      : data;

    if (!checkDataType(processedData)) {
      throw new Error(
        "The type of the data found doesn't match the expected one",
      );
    }

    return processedData as T;
  } catch (error) {
    onError?.(error as Error);
    return;
  }
};

const removeFromStorage = (
  key: string,
  storage: 'local' | 'session',
  options: RemoveOptions = {},
): void => {
  const { onError } = options;

  try {
    (storage === 'local'
      ? window.localStorage
      : window.sessionStorage
    ).removeItem(key);
  } catch (error) {
    onError?.(error as Error);
  }
};

export const saveInLocalStorage = <T>(
  key: string,
  data: T,
  options?: SaveOptions,
) => saveInStorage<T>(key, data, 'local', options);

export const retrieveFromLocalStorage = <T>(
  key: string,
  options?: RetrieveOptions<T>,
) => retrieveFromStorage<T>(key, 'local', options);

export const removeFromLocalStorage = (key: string, options?: RemoveOptions) =>
  removeFromStorage(key, 'local', options);

export const saveInSessionStorage = <T>(
  key: string,
  data: T,
  options: SaveOptions = {},
): void => saveInStorage<T>(key, data, 'session', options);

export const retrieveFromSessionStorage = <T>(
  key: string,
  options: RetrieveOptions<T> = {},
): T | undefined => retrieveFromStorage<T>(key, 'session', options);

export const removeFromSessionStorage = (
  key: string,
  options?: RemoveOptions,
) => removeFromStorage(key, 'session', options);
